/**
 * proposeCampaign — RPC contract for the callable that composes one event's
 * ad and parks it for the operator to answer.
 *
 * The brief is one number. Objective, audience, placements, bid strategy,
 * optimisation event, schedule and landing page are every one derived from
 * the event and its own catalogue, so none of them crosses this wire; the
 * operator has none of that vocabulary and would be guessing at all of it.
 * The two dials they genuinely own — the radius and the minimum age — are
 * not here either: they sit on the proposal beside the sentence they change,
 * written under the campaign's `['proposal', 'budgetMinor']` mask once there
 * is something to look at. A question asked before anything is rendered is
 * asked blind.
 *
 * The input names the campaign and carries no part of its document, the
 * constraint `launchCampaign.action.ts` states one door over: the
 * composition reads the stored draft's own brief and this event's own live
 * catalogue, and a caller-supplied copy of either would let a caller compose
 * against a world they described rather than the one that exists. The server
 * reads the document; the caller names it.
 *
 * The budget is the one exception and it is not one: it is the value under
 * judgment, never the judgment's terms. Both bounds it is measured against —
 * Meta's minimum for this ad account, fetched per call, and the workspace's
 * own ceiling at its settings singleton — are read server-side, so naming an
 * amount cannot move the bar the amount is held to.
 *
 * Fields justified:
 *   eventId     — the event this campaign promotes; the path is
 *                 workspaces/{orgId}/events/{eventId}/campaigns/{campaignId}.
 *   campaignId  — the draft to compose into. It already exists, carrying the
 *                 operator's brief written under the `['brief']` mask.
 *   budgetMinor — the whole brief, and the one operator-authored fact with no
 *                 home on the document yet: `campaign.entity.ts` keeps money
 *                 off `brief` deliberately, so the amount arrives here and
 *                 lands on the top-level field the mask and the ceiling check
 *                 can both see.
 * Excluded:
 *   the brief's own words — already on the draft this call names; a copy
 *           travelling beside the call is free to disagree with the record
 *           the composition actually reads.
 *   radiusKm, minAge — the operator's two dials, and they belong on the
 *           proposal rather than in the brief: they change a sentence that
 *           does not exist yet.
 *   objective, audience, placements, bid strategy, optimisation event — every
 *           one derived, and the reasoning that fixes each is the design's
 *           rather than a caller's.
 *   endsAt, priceFloorCents, currency — derived at compose time from the
 *           event's own sales windows, its catalogue and the ad account. A
 *           caller-supplied copy of any of them would let the ad quote a price
 *           the shop does not charge, count in a unit the account does not
 *           bill in, or run past the last instant a ticket can be bought.
 *   a creative source — the three sources stand as peers and the composition
 *           produces all of them; which one runs is the proposal's `assetId`,
 *           chosen after there is something to look at.
 *   a re-draft mode — the campaign's own stored status is what separates a
 *           first composition from a re-draft, and the entity already refuses
 *           a re-draft outside `proposed`; a caller-set mode could only ask
 *           for the composition that state refuses.
 *
 * Output: a discriminated union on `outcome` — the composition's own answer,
 * with each way the event or the workspace refuses it as its own named
 * member. An unpermitted caller still throws `HttpsError`; that is a closed
 * door rather than an answer.
 *
 * THE SUCCESS MEMBER CARRIES NOTHING. The proposal, the instant it parked,
 * the floor its words quote, the end instant and the currency all land on the
 * campaign document, which the console reading this answer is already
 * subscribed to; carrying them back would be a second representation of one
 * fact, free to disagree with the record.
 *
 * A refusal writes nothing at all, so it is the one branch that has to carry
 * its own substance — and it carries only what no document the caller can
 * read already holds. The workspace ceiling is its own readable singleton
 * (`ad-spend-ceiling.entity.ts`, read under this same token), so it never
 * travels. Meta's minimum for this ad account is fetched per call and stored
 * nowhere, so it does — with the unit it counts in beside it, because an
 * amount with no currency is not an amount, and the ad account is free to
 * bill in one the event's own euro prices are not in.
 *
 * ONE FLAT UNION, not `refused` beside a reason enum. Four refusals carry
 * nothing and the fifth carries the bound it measured against; a tag beside
 * an optional bag would leave a minimum representable on a refusal that never
 * fetched one, and `refused` cannot hold five members at one discriminant.
 * Nothing is lost: each refusal has its own remedy — put a type on sale, fill
 * in the workspace's legal details, set a ceiling, lower the amount, raise it
 * to what Meta will take — so the surface switches per member and there is no
 * generic refused face to draw.
 *
 * Members:
 *   proposed                   — the ad is composed and parked; the campaign
 *                                now carries everything the operator answers.
 *   nothingPubliclyPurchasable — no ticket type on this event resolves to
 *                                `buyer` and stands open, so there is nothing
 *                                an ad could send anyone to buy.
 *   workspaceLegalMissing      — the workspace states no legal details, and
 *                                the ad set names a payor and a beneficiary
 *                                Meta refuses as absent.
 *   adSpendCeilingUnset        — no bound on ad money exists for this
 *                                workspace, and no spend may commit without
 *                                one.
 *   budgetOverCeiling          — the amount is above that bound.
 *   budgetUnderMetaMinimum     — the amount is below what this ad account can
 *                                run at, which only Meta knows and only this
 *                                answer can say.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

// Money at the ad network, an integer in the ad account's own minor unit —
// never `Cents`, whose own describe names euro cents, because the account
// this money leaves is free to bill in another currency
// (workspace.entity.ts's MetaAdvertiserSchema.adAccountId says exactly that).
// Positive rather than `.min(0)`: a campaign RECORDS a figure and zero is a
// legitimate record, but this shape is an instruction to spend, and an
// instruction to spend nothing is the absence of the instruction.
const BudgetMinor = z.number().int().positive();

export const ProposeCampaignInput = z
	.object({
		orgId: Id,
		eventId: Id.describe("the event this campaign promotes"),
		campaignId: Id.describe("the draft campaign to compose the ad into"),
		budgetMinor: BudgetMinor.describe(
			"the lifetime budget the operator is willing to commit, in the ad account's own minor unit — a lifetime budget, never a daily one, because a daily budget is not a cap and this product tells an operator what a campaign costs",
		),
	})
	.strict();
export type ProposeCampaignInput = z.infer<typeof ProposeCampaignInput>;

export const ProposeCampaignOutput = z.discriminatedUnion("outcome", [
	z.object({ outcome: z.literal("proposed") }).strict(),
	z.object({ outcome: z.literal("nothingPubliclyPurchasable") }).strict(),
	z.object({ outcome: z.literal("workspaceLegalMissing") }).strict(),
	z.object({ outcome: z.literal("adSpendCeilingUnset") }).strict(),
	z.object({ outcome: z.literal("budgetOverCeiling") }).strict(),
	z
		.object({
			outcome: z.literal("budgetUnderMetaMinimum"),
			minimumBudgetMinor: BudgetMinor.describe(
				"the least this ad account can run a campaign on, as Meta reported it at this call, in that account's own minor unit",
			),
			currency: z
				.string()
				.length(3)
				.describe(
					"ISO 4217, read from the ad account — the unit the minimum counts in, and the unit a budget offered against it must count in too",
				),
		})
		.strict(),
]);
export type ProposeCampaignOutput = z.infer<typeof ProposeCampaignOutput>;
